import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';

const ProblemFormModal = ({ problem, onClose, onSubmit }) => {
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    domain: 'mechanical',
    priority: 'medium',
    status: 'Not Decided',
    dueDate: '',
    notes: '',
    solutions: [],
    subtasks: [],
    project_id: ''
  });
  // If the task has a saved done_date, use it. Otherwise default to today.
  const [doneDate, setDoneDate] = useState(
    problem?.done_date ? new Date(problem.done_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);

  // Fetch all projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data, error } = await supabase
          .from('experiments')
          .select('id, title')
          .eq('type', 'project')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setProjects(data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    
    fetchProjects();
  }, []);

  useEffect(() => {
    const loadProblemData = async () => {
      if (problem) {
        console.log('ProblemFormModal: Loading problem data for:', problem.id);

        // Load basic problem data - PRESERVE ALL ORIGINAL FIELDS
        const basicData = {
          ...problem,  // Keep all original fields including id
          dueDate: problem.due_date || problem.dueDate || '',
          solutions: [],  // Start with empty solutions array
          subtasks: []    // Start with empty subtasks array
        };

        // Initialize doneDate from problem or set to today
        if (problem.done_date) {
          setDoneDate(new Date(problem.done_date).toISOString().split('T')[0]);
        } else {
          setDoneDate(new Date().toISOString().split('T')[0]);
        }

        // Safe isEditMode check
        const isEditMode = !!(problem && problem.id);

        if (isEditMode) {
          // For editing: Handle both UI-created and CSV-imported problems
          if (problem.solutions && problem.solutions.length > 0) {
            // Problem already has solutions loaded (from fetchProblems with LEFT JOIN)
            basicData.solutions = problem.solutions
              .filter(sol => sol && sol.description && sol.description.trim() !== '') // Filter out empty solutions
              .map(sol => ({
                description: sol.description.trim(),
                isChosen: sol.is_chosen || false
              }));
            console.log('ProblemFormModal: Loaded existing solutions:', basicData.solutions);
          } else {
            // CSV-imported problem or problem without solutions - start with empty array
            basicData.solutions = [];
            console.log('ProblemFormModal: No existing solutions found, starting with empty array');
          }

          // Load subtasks similarly
          if (problem.subtasks && problem.subtasks.length > 0) {
            basicData.subtasks = problem.subtasks
              .filter(st => st && st.title && st.title.trim() !== '')
              .map(st => ({
                id: st.id,
                title: st.title.trim(),
                due_date: st.due_date || '',
                is_completed: st.is_completed || false,
                solution: st.solution || '',
                assigned_to: st.assigned_to || null,  // Preserve assignment
                completed_date: st.completed_date || null  // Preserve completion date
              }));
            console.log('ProblemFormModal: Loaded existing subtasks:', basicData.subtasks);
          } else {
            basicData.subtasks = [];
            console.log('ProblemFormModal: No existing subtasks found, starting with empty array');
          }
        }

        console.log('ProblemFormModal: Setting form data with solutions:', basicData.solutions);
        setFormData(basicData);
      } else {
        // Reset to initial state for new problem
        setFormData({
          title: '',
          description: '',
          domain: 'mechanical',
          priority: 'medium',
          status: 'Not Decided',
          dueDate: '',
          notes: '',
          solutions: [],
          subtasks: []
        });
        // Reset doneDate to today for new problems
        setDoneDate(new Date().toISOString().split('T')[0]);
      }
    };

    loadProblemData();
  }, [problem]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddSolution = () => {
    setFormData({
      ...formData,
      solutions: [{ description: '', isChosen: false }, ...formData.solutions]
    });
  };

  const handleRemoveSolution = (index) => {
    setFormData({
      ...formData,
      solutions: formData.solutions.filter((_, i) => i !== index)
    });
  };

  const handleSolutionChange = (index, field, value) => {
    const updated = formData.solutions.map((sol, i) => {
      if (i === index) {
        return { ...sol, [field]: value };
      }
      return sol;
    });
    setFormData({ ...formData, solutions: updated });
  };

  const handleAddSubtask = () => {
    setFormData({
      ...formData,
      subtasks: [{ title: '', due_date: '', is_completed: false, solution: '', assigned_to: null, completed_date: null }, ...formData.subtasks]
    });
  };

  const handleRemoveSubtask = (index) => {
    setFormData({
      ...formData,
      subtasks: formData.subtasks.filter((_, i) => i !== index)
    });
  };

  const handleSubtaskChange = (index, field, value) => {
    const updated = formData.subtasks.map((st, i) => {
      if (i === index) {
        // If marking as completed, set completed_date to now
        // If unmarking, clear completed_date
        if (field === 'is_completed') {
          return { 
            ...st, 
            [field]: value,
            completed_date: value ? new Date().toISOString() : null
          };
        }
        return { ...st, [field]: value };
      }
      return st;
    });
    setFormData({ ...formData, subtasks: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const operationId = `form-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log(`=== FORM SUBMIT STARTED [${operationId}] ===`);
    console.log(`🔍 [${operationId}] Timestamp:`, new Date().toISOString());
    console.log(`🔍 [${operationId}] Form Data:`, formData);
    console.log(`🔍 [${operationId}] Problem prop:`, problem);
    console.log(`🔍 [${operationId}] Is Edit Mode:`, !!(problem && problem.id));
    console.log(`🔍 [${operationId}] Loading state:`, loading);

    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    // Prevent double submission
    if (loading) {
      console.log(`⚠️ [${operationId}] Already submitting, ignoring duplicate call`);
      return;
    }

    setLoading(true);

    try {
      // Filter out empty solutions before submitting
      const validSolutions = formData.solutions.filter(sol =>
        sol && sol.description && sol.description.trim() !== ''
      );

      // Filter out empty subtasks before submitting
      const validSubtasks = formData.subtasks.filter(st =>
        st && st.title && st.title.trim() !== ''
      );

      const submissionData = {
        ...formData,
        solutions: validSolutions,
        subtasks: validSubtasks,
        done_date: formData.status === 'Done' ? doneDate : null
      };

      console.log(`🚀 [${operationId}] ProblemFormModal: Submitting form data:`, submissionData);
      console.log(`🚀 [${operationId}] ProblemFormModal: Original solutions count:`, formData.solutions.length);
      console.log(`🚀 [${operationId}] ProblemFormModal: Valid solutions count:`, validSolutions.length);
      console.log(`🚀 [${operationId}] ProblemFormModal: Valid solutions:`, validSolutions);
      console.log(`🚀 [${operationId}] ProblemFormModal: Calling onSubmit...`);

      await onSubmit(submissionData);

      console.log(`✅ [${operationId}] ProblemFormModal: onSubmit completed successfully`);
    } catch (error) {
      console.error(`❌ [${operationId}] ProblemFormModal: Error in handleSubmit:`, error);
      alert(`Failed to save: ${error.message}`);
    } finally {
      setLoading(false);
      console.log(`=== FORM SUBMIT ENDED [${operationId}] ===`);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="problem-form-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold" data-testid="form-modal-title">
              {problem ? 'Edit Problem' : 'Create New Problem'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="form-modal-close-btn"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 scrollbar-thin" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full glass-input"
              placeholder="Enter problem title"
              required
              data-testid="form-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="4"
              placeholder="Enter detailed description (optional)"
              data-testid="form-description-input"
            />
          </div>

          {/* Domain & Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Domain <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-domain-select"
                >
                  <option value="mechanical" className="bg-[#1a1f2e] text-white">Mechanical</option>
                  <option value="software" className="bg-[#1a1f2e] text-white">Software</option>
                  <option value="electronics" className="bg-[#1a1f2e] text-white">Electronics</option>
                  <option value="operations" className="bg-[#1a1f2e] text-white">Operations</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Priority <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-priority-select"
                >
                  <option value="low" className="bg-[#1a1f2e] text-white">Low</option>
                  <option value="medium" className="bg-[#1a1f2e] text-white">Medium</option>
                  <option value="high" className="bg-[#1a1f2e] text-white">High</option>
                  <option value="critical" className="bg-[#1a1f2e] text-white">Critical</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Link to Project <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <div className="relative">
              <select
                name="project_id"
                value={formData.project_id || ''}
                onChange={handleChange}
                className="w-full glass-input cursor-pointer pr-10"
                data-testid="form-project-select"
              >
                <option value="" className="bg-[#1a1f2e] text-white">No Project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id} className="bg-[#1a1f2e] text-white">
                    {project.title}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            <p className="text-xs text-gray-500 mt-1">Link this task to a project for better tracking</p>
          </div>

          {/* Status & Due Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Status <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-status-select"
                >
                  <option value="Not Decided" className="bg-[#1a1f2e] text-white">Not Decided</option>
                  <option value="In Progress" className="bg-[#1a1f2e] text-white">In Progress</option>
                  <option value="Hold" className="bg-[#1a1f2e] text-white">Hold</option>
                  <option value="Done" className="bg-[#1a1f2e] text-white">Done</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="w-full glass-input"
                data-testid="form-due-date-input"
              />
            </div>
          </div>

          {/* Done Date - Only show when status is 'Done' */}
          {formData.status === 'Done' && (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-gray-400">Completed On</label>
              <input
                type="date"
                value={doneDate}
                onChange={(e) => setDoneDate(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white text-sm rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors"
                data-testid="form-done-date-input"
              />
            </div>
          )}

          {/* Solutions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium">Solutions</label>
            </div>
            <button
              type="button"
              onClick={handleAddSolution}
              className="w-full py-2 border border-dashed border-gray-500 rounded-lg text-gray-400 hover:text-white hover:border-white hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm mb-3"
              data-testid="add-solution-btn"
            >
              <Plus size={16} />
              Add Solution
            </button>
            <div className="space-y-3 mb-3">
              {formData.solutions.map((solution, index) => (
                <div key={index} className="glass-card p-4" data-testid={`solution-form-${index}`}>
                  <textarea
                    value={solution.description}
                    onChange={(e) => handleSolutionChange(index, 'description', e.target.value)}
                    className="w-full glass-input resize-none mb-2"
                    rows="2"
                    placeholder="Enter solution description"
                    data-testid={`solution-description-${index}`}
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={solution.isChosen}
                        onChange={(e) => handleSolutionChange(index, 'isChosen', e.target.checked)}
                        className="w-4 h-4"
                        data-testid={`solution-chosen-${index}`}
                      />
                      <span className="text-sm">Chosen Solution</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemoveSolution(index)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`solution-remove-${index}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subtasks */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium">Subtasks</label>
            </div>
            <button
              type="button"
              onClick={handleAddSubtask}
              className="w-full py-2 border border-dashed border-gray-500 rounded-lg text-gray-400 hover:text-white hover:border-white hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm mb-3"
              data-testid="add-subtask-btn"
            >
              <Plus size={16} />
              Add Subtask
            </button>
            <div className="space-y-3 mb-3">
              {formData.subtasks.map((subtask, originalIndex) => ({ subtask, originalIndex })).sort((a, b) => {
                // Incomplete tasks first
                if (!a.subtask.is_completed && b.subtask.is_completed) return -1;
                if (a.subtask.is_completed && !b.subtask.is_completed) return 1;
                
                // Both completed - sort by completed_date (most recent first)
                if (a.subtask.is_completed && b.subtask.is_completed) {
                  const dateA = a.subtask.completed_date ? new Date(a.subtask.completed_date).getTime() : 0;
                  const dateB = b.subtask.completed_date ? new Date(b.subtask.completed_date).getTime() : 0;
                  return dateB - dateA; // Most recent first
                }
                
                // Both incomplete - maintain original order
                return 0;
              }).map(({ subtask, originalIndex }) => (
                <div key={originalIndex} className="glass-card p-4" data-testid={`subtask-form-${originalIndex}`}>
                  <input
                    type="text"
                    value={subtask.title}
                    onChange={(e) => handleSubtaskChange(originalIndex, 'title', e.target.value)}
                    className="w-full glass-input mb-2"
                    placeholder="Enter subtask title"
                    data-testid={`subtask-title-${originalIndex}`}
                  />
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-400 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={subtask.due_date}
                        onChange={(e) => handleSubtaskChange(originalIndex, 'due_date', e.target.value)}
                        className="w-full glass-input text-sm"
                        data-testid={`subtask-due-date-${originalIndex}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveSubtask(originalIndex)}
                      className="text-red-400 hover:text-red-300 mt-5"
                      data-testid={`subtask-remove-${originalIndex}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={subtask.is_completed}
                        onChange={(e) => handleSubtaskChange(originalIndex, 'is_completed', e.target.checked)}
                        className="w-4 h-4"
                        data-testid={`subtask-completed-${originalIndex}`}
                      />
                      <span className="text-sm">Completed</span>
                    </label>
                  </div>
                  {subtask.is_completed && (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Completed Date</label>
                        <input
                          type="date"
                          value={subtask.completed_date ? new Date(subtask.completed_date).toISOString().split('T')[0] : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value ? new Date(e.target.value).toISOString() : new Date().toISOString();
                            handleSubtaskChange(originalIndex, 'completed_date', dateValue);
                          }}
                          className="w-full glass-input text-sm"
                          data-testid={`subtask-completed-date-${originalIndex}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Solution / Outcome</label>
                        <textarea
                          value={subtask.solution || ''}
                          onChange={(e) => handleSubtaskChange(originalIndex, 'solution', e.target.value)}
                          className="w-full glass-input text-sm"
                          rows="2"
                          placeholder="Describe what was done to complete this subtask..."
                          data-testid={`subtask-solution-${originalIndex}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="3"
              placeholder="Enter additional notes"
              data-testid="form-notes-input"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
            data-testid="form-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 rounded text-white font-medium transition-colors ${loading
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-500'
              }`}
            data-testid="form-save-btn"
          >
            {loading ? 'Saving...' : (problem ? 'Update' : 'Create')}
          </button>
        </div>

        {/* Last Updated Metadata - Only show in edit mode, at the very bottom */}
        {problem && problem.updated_at && (
          <div className="px-6 pb-6 border-t border-white/10 pt-4">
            <p className="text-xs text-gray-500 text-center" data-testid="form-last-updated">
              {problem.updater && problem.updater.name
                ? `Last updated by ${problem.updater.name} on ${format(new Date(problem.updated_at), 'MMM dd')} at ${format(new Date(problem.updated_at), 'h:mm a')}`
                : `Last updated on ${format(new Date(problem.updated_at), 'MMM dd')} at ${format(new Date(problem.updated_at), 'h:mm a')}`
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemFormModal;
